import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCcw, ExternalLink } from 'lucide-react';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { Button } from '../../components/ui/Button';
import { Dialog, DialogFooter } from '../../components/ui/Dialog';
import { AdminShell } from '../../components/AdminShell';
import { useToast } from '../../components/ui/Toast';
import { formatDate } from '../../lib/format';

type Severity = 'minor' | 'major' | 'critical';
type Status = 'investigating' | 'identified' | 'monitoring' | 'resolved';

interface Incident {
  id: string;
  title: string;
  summary: string | null;
  severity: Severity;
  status: Status;
  postmortem_url: string | null;
  occurred_at: string;
  resolved_at: string | null;
}

const SEVERITY_TONE: Record<Severity, 'muted' | 'accent' | 'warn'> = {
  minor: 'muted',
  major: 'accent',
  critical: 'warn',
};

/** Admin → Incidents. Create + edit public incidents that surface on /status. */
export function AdminIncidentsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [openCreate, setOpenCreate] = useState(false);

  const rows = useQuery<Incident[]>({
    queryKey: ['admin-incidents'],
    queryFn: async () => (await api.get('/incidents?limit=50')).data,
    refetchInterval: 60_000,
  });

  const create = useMutation({
    mutationFn: async (body: Partial<Incident> & { occurred_at: string }) =>
      (await api.post('/incidents', body)).data,
    onSuccess: () => {
      setOpenCreate(false);
      qc.invalidateQueries({ queryKey: ['admin-incidents'] });
    },
    onError: () => toast.error(t('admin.incidents.createError')),
  });

  const update = useMutation({
    mutationFn: async (input: { id: string; patch: Partial<Incident> }) =>
      (await api.patch(`/incidents/${input.id}`, input.patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-incidents'] }),
    onError: () => toast.error(t('admin.incidents.updateError')),
  });

  return (
    <AdminShell>
      <div className="px-8 py-7 max-w-7xl w-full mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500">{t('admin.eyebrow')}</div>
            <h1 className="display text-3xl font-semibold tracking-tight mt-1">{t('admin.incidents.title')}</h1>
            <p className="text-sm text-ink-600 dark:text-ink-300 mt-1 max-w-2xl">{t('admin.incidents.subtitle')}</p>
          </div>
          <Button onClick={() => setOpenCreate(true)}>
            <Plus size={14} /> {t('admin.incidents.create')}
          </Button>
        </div>

        <Card className="mt-6 overflow-hidden">
          {rows.isLoading ? (
            <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
          ) : !rows.data || rows.data.length === 0 ? (
            <div className="p-10 text-center text-ink-500 text-sm">{t('admin.incidents.empty')}</div>
          ) : (
            <ul className="divide-y divide-ink-200 dark:divide-ink-800">
              {rows.data.map((inc) => (
                <li key={inc.id} className="px-5 py-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{inc.title}</div>
                      <div className="text-xs text-ink-500 mt-0.5 num">
                        {formatDate(inc.occurred_at, i18n.language)}
                        {inc.resolved_at && ` · ${t('status.resolvedAt')} ${formatDate(inc.resolved_at, i18n.language)}`}
                      </div>
                      {inc.summary && (
                        <p className="mt-2 text-sm text-ink-600 dark:text-ink-300 leading-relaxed line-clamp-3">{inc.summary}</p>
                      )}
                      {inc.postmortem_url && (
                        <a href={inc.postmortem_url} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1 text-xs text-accent-700 hover:underline mt-2">
                          {t('admin.incidents.postmortem')} <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Pill tone={SEVERITY_TONE[inc.severity]}>{t(`status.severity.${inc.severity}`)}</Pill>
                      <select
                        value={inc.status}
                        onChange={(e) => {
                          const next = e.target.value as Status;
                          update.mutate({
                            id: inc.id,
                            patch: {
                              status: next,
                              ...(next === 'resolved' && !inc.resolved_at ? { resolved_at: new Date().toISOString() } : {}),
                            } as Partial<Incident>,
                          });
                        }}
                        className="text-xs rounded-md border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-2 py-1"
                      >
                        <option value="investigating">{t('status.incidentStatus.investigating')}</option>
                        <option value="identified">{t('status.incidentStatus.identified')}</option>
                        <option value="monitoring">{t('status.incidentStatus.monitoring')}</option>
                        <option value="resolved">{t('status.incidentStatus.resolved')}</option>
                      </select>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <button
          onClick={() => rows.refetch()}
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-ink-500 hover:text-ink-900 dark:hover:text-ink-100"
        >
          <RefreshCcw size={12} /> {t('common.refresh')}
        </button>
      </div>

      <CreateIncidentDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        onSubmit={(payload) => create.mutate(payload)}
        busy={create.isPending}
      />
    </AdminShell>
  );
}

function CreateIncidentDialog({
  open,
  onOpenChange,
  onSubmit,
  busy,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onSubmit: (payload: { title: string; summary?: string; severity: Severity; status: Status; occurred_at: string; postmortem_url?: string }) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [severity, setSeverity] = useState<Severity>('minor');
  const [status, setStatus] = useState<Status>('investigating');
  const [postmortem, setPostmortem] = useState('');

  function submit() {
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      summary: summary.trim() || undefined,
      severity,
      status,
      occurred_at: new Date().toISOString(),
      postmortem_url: postmortem.trim() || undefined,
    });
    setTitle(''); setSummary(''); setSeverity('minor'); setStatus('investigating'); setPostmortem('');
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('admin.incidents.dialogTitle')} description={t('admin.incidents.dialogBody')}>
      <div className="space-y-4">
        <FieldText label={t('admin.incidents.fieldTitle')} value={title} onChange={setTitle} placeholder={t('admin.incidents.fieldTitlePlaceholder')} />
        <FieldArea label={t('admin.incidents.fieldSummary')} value={summary} onChange={setSummary} />
        <div className="grid grid-cols-2 gap-3">
          <FieldSelect label={t('admin.incidents.fieldSeverity')} value={severity} onChange={(v) => setSeverity(v as Severity)}
            options={[['minor', t('status.severity.minor')], ['major', t('status.severity.major')], ['critical', t('status.severity.critical')]]} />
          <FieldSelect label={t('admin.incidents.fieldStatus')} value={status} onChange={(v) => setStatus(v as Status)}
            options={[['investigating', t('status.incidentStatus.investigating')], ['identified', t('status.incidentStatus.identified')], ['monitoring', t('status.incidentStatus.monitoring')], ['resolved', t('status.incidentStatus.resolved')]]} />
        </div>
        <FieldText label={t('admin.incidents.fieldPostmortem')} value={postmortem} onChange={setPostmortem} placeholder="https://plyez.com/postmortems/2026-05-24" />
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={!title.trim() || busy}>{busy ? t('common.loading') : t('common.create')}</Button>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
      </DialogFooter>
    </Dialog>
  );
}

function FieldText({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full h-9 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm" />
    </label>
  );
}
function FieldArea({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{label}</div>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm" />
    </label>
  );
}
function FieldSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: Array<[string, string]> }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">{label}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 text-sm">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
